import { useQuery } from '@tanstack/react-query'
import type {
  AnalyticsResponse,
  OverviewData,
  TrafficData,
  ReleaseBandsData,
  FeaturesData,
  FeatureTimelineData,
  RegionsData,
  DevicesData,
  HourlyData,
  MonthlyComparisonData,
  Granularity,
} from './types'
import { buildApiUrl } from './api-url'

function getToken(): string | null {
  return localStorage.getItem('analytics_dashboard_token')
}

async function fetchWithAuth<T>(
  endpoint: string,
  params?: Record<string, string>
): Promise<AnalyticsResponse<T>> {
  const token = getToken()
  if (!token) {
    throw new Error('Not authenticated')
  }

  const response = await fetch(buildApiUrl(endpoint, params), {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.error?.message || `HTTP ${response.status}`)
  }

  return response.json()
}

// Overview query
export function useOverview(startDate: string, endDate: string) {
  return useQuery({
    queryKey: ['analytics', 'overview', startDate, endDate],
    queryFn: () =>
      fetchWithAuth<OverviewData>('/api/analytics/overview', {
        start_date: startDate,
        end_date: endDate,
      }),
  })
}

// Traffic query
export function useTraffic(
  startDate: string,
  endDate: string,
  granularity: Granularity = 'day'
) {
  return useQuery({
    queryKey: ['analytics', 'traffic', startDate, endDate, granularity],
    queryFn: () =>
      fetchWithAuth<TrafficData>('/api/analytics/traffic', {
        start_date: startDate,
        end_date: endDate,
        granularity,
      }),
  })
}

// Release bands query
export function useReleaseBands(startDate: string, endDate: string) {
  return useQuery({
    queryKey: ['analytics', 'releases', startDate, endDate],
    queryFn: () =>
      fetchWithAuth<ReleaseBandsData>('/api/analytics/releases', {
        start_date: startDate,
        end_date: endDate,
      }),
  })
}

// Features query
export function useFeatures(startDate: string, endDate: string) {
  return useQuery({
    queryKey: ['analytics', 'features', startDate, endDate],
    queryFn: () =>
      fetchWithAuth<FeaturesData>('/api/analytics/features', {
        start_date: startDate,
        end_date: endDate,
      }),
  })
}

// Feature timeline query
export function useFeatureTimeline(
  startDate: string,
  endDate: string,
  granularity: 'day' | 'week' = 'day'
) {
  return useQuery({
    queryKey: ['analytics', 'features', 'timeline', startDate, endDate, granularity],
    queryFn: () =>
      fetchWithAuth<FeatureTimelineData>('/api/analytics/features/timeline', {
        start_date: startDate,
        end_date: endDate,
        granularity,
      }),
  })
}

// Regions query
export function useRegions(startDate: string, endDate: string) {
  return useQuery({
    queryKey: ['analytics', 'regions', startDate, endDate],
    queryFn: () =>
      fetchWithAuth<RegionsData>('/api/analytics/regions', {
        start_date: startDate,
        end_date: endDate,
      }),
  })
}

// Devices query
export function useDevices(startDate: string, endDate: string) {
  return useQuery({
    queryKey: ['analytics', 'devices', startDate, endDate],
    queryFn: () =>
      fetchWithAuth<DevicesData>('/api/analytics/devices', {
        start_date: startDate,
        end_date: endDate,
      }),
  })
}

// Hourly patterns query
export function useHourly(startDate: string, endDate: string) {
  return useQuery({
    queryKey: ['analytics', 'hourly', startDate, endDate],
    queryFn: () =>
      fetchWithAuth<HourlyData>('/api/analytics/hourly', {
        start_date: startDate,
        end_date: endDate,
      }),
  })
}

// Monthly comparison query
export function useMonthly() {
  return useQuery({
    queryKey: ['analytics', 'monthly'],
    queryFn: () => fetchWithAuth<MonthlyComparisonData>('/api/analytics/monthly'),
  })
}
